import React, { Fragment, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import _ from "lodash"
import moment from "moment"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { Box, Typography, Avatar, Grid } from "@material-ui/core"
import AddComment from "./comment/AddComment"
import { getComments, deleteComment as deleteCommentById } from "../../../actions/documentsActions"
import ConfirmationDialog from "../../common/dialog/ConfirmationDialog"
import { ADD_ANNOTATION, REMOVE_ANNOTATION } from "../../../types"
import { deleteAnnotation, updateViewerPageNumber } from "../../../actions/viewerActions"
import { getFileRequestByPage } from "../../../actions/documentModalActions"
import { LOCAL_STORAGE_KEYS } from "../../../utils"

const useStyles = makeStyles((theme) => ({
	root: {
		marginBottom: 10,
		backgroundColor: theme.palette.grey[50],
		border: "none",
		"& .MuiButtonBase-root": {
			textTransform: "capitalize",
			fontSize: theme.typography.caption.fontSize,
		},
		"& .MuiCardHeader-root": {
			paddingBottom: 8,
		},
		"& .MuiCardContent-root": {
			paddingTop: 0,
			paddingBottom: 12,
		},
		"& .MuiCardActions-root > .MuiButtonBase-root": {
			backgroundColor: theme.palette.grey[100],
		},
	},
	commentBox: {
		height: "38vh",
		overflowY: "auto",
		marginBottom: 20,
	},
	commentDate: {
		...theme.typography.caption,
		color: theme.palette.grey["D100"],
		lineHeight: "20px",
	},
	userName: {
		...theme.typography.subtitle2,
		fontWeight: theme.typography.fontWeightMedium,
	},
	commentAction: {
		cursor: "pointer",
		"&:hover": {
			textDecoration: "underline",
		},
		textDecoration: "none",
		...theme.typography.subtitle2,
		color: theme.palette.grey["D100"],
		fontSize: "12px",
	},
	commentContent: {
		wordWrap: "break-word",
		wordBreak: "break-all",
	},
	listContainer: {
		padding: "0px 10px 10px 10px",
	},
	annoListContainer: {
		padding: 10,
		marginBottom: 5,
	},
}))

function Comments(props) {
	const classes = useStyles()
	const dispatch = useDispatch()
	const [comments, setComments] = useState([])
	const theme = useTheme()
	const [open, setOpen] = useState(false)
	const [commentId, setCommentId] = useState("")
	const [annotations, setAnnotations] = useState([])
	const [editCommentData, setEditCommentData] = useState({})
	const [t] = useTranslation("global")

	const { documentDetails } = useSelector((state) => state.documentModal)
	const { user, loggedIn } = useSelector((state) => state.authtentication)
	const currentPageNumber = useSelector((state) => state.viewerData.currentPageNumber)
	const annotationData = useSelector((state) => state.viewerData.annotations)
	const isNativeView = useSelector((state) => state.viewerData.isNativeView)
	const formatDate = (date) => {
		return `${moment(moment.utc(date)).local().format("LL")} at ${moment(moment.utc(date)).local().format("h:mma")}`
	}

	const deleteComment = (id) => {
		setOpen(true)
		setCommentId(id)
	}

	const handleDeleteAnnotation = (id, isNativeView) => {
		dispatch(deleteAnnotation(id, isNativeView))
	}

	const editComment = (id, commentTxt) => {
		if (id && commentTxt) {
			setEditCommentData({
				commentId: id,
				comment: commentTxt,
			})
		}
	}

	useEffect(() => {
		if (annotationData) {
			if (_.isArray(annotationData)) {
				annotationData?.sort((a, b) => a.page_number - b.page_number)
				setAnnotations(annotationData)
			}
		}
	}, [annotationData])

	const onConfirm = () => {
		const docId = props?.documentId
		if (docId && commentId) {
			dispatch(deleteCommentById(commentId, docId))
			setOpen(false)
			//register comment is deleted
			localStorage.setItem(LOCAL_STORAGE_KEYS.TRIGGER_P_SEARCH, true)
		}
	}

	const onDismiss = () => {
		setOpen(false)
	}

	function stringToColor(string) {
		let hash = 0
		let i
		for (i = 0; i < string.length; i += 1) {
			hash = string.charCodeAt(i) + ((hash << 5) - hash)
		}
		let color = "#"
		for (i = 0; i < 3; i += 1) {
			const value = (hash >> (i * 8)) & 0xff
			color += `00${value.toString(16)}`.slice(-2)
		}
		return color
	}

	function getContrast(hexcolor) {
		if (hexcolor.slice(0, 1) === "#") {
			hexcolor = hexcolor.slice(1)
		}
		var r = parseInt(hexcolor.substr(0, 2), 16)
		var g = parseInt(hexcolor.substr(2, 2), 16)
		var b = parseInt(hexcolor.substr(4, 2), 16)
		var yiq = (r * 299 + g * 587 + b * 114) / 1000
		return yiq >= 128 ? theme.palette.text.primary : theme.palette.background.default
	}

	const stringAvatar = (name) => {
		return {
			style: {
				...theme?.typography?.avtarText,
				background: stringToColor(name),
				color: getContrast(stringToColor(name)),
				width: "26px",
				height: "26px",
				lineHeight: "10px",
			},
			children: name?.split(" ")[1] ? `${name?.split(" ")[0][0]}${name?.split(" ")[1][0]}` : name?.split(" ")[0][0],
		}
	}

	useEffect(() => {
		if (_.isEmpty(documentDetails?.comments)) {
			dispatch(getComments(props?.documentId))
		}

		return () => {
			if (commentId) setCommentId("")
		}
	}, [])

	useEffect(() => {
		setComments(documentDetails?.comments)
	}, [documentDetails?.comments])

	return (
		<Fragment>
			<Box className={classes.commentBox}>
				{comments?.length > 0 &&
					comments?.map((comment, index) => {
						return (
							<Grid container spacing={1} xs={12} key={`${comment?.id}-${index}`} className={classes.listContainer}>
								<Grid item xs={2}>
									<Avatar alt={"A"} {...stringAvatar(`${comment?.first_name} ${comment?.last_name}`)} />
								</Grid>
								<Grid item xs={10} style={{ paddingLeft: 0 }}>
									<Grid item container>
										<Grid item xs={12}>
											<Typography
												title={`${comment?.first_name} ${comment?.last_name}`}
												noWrap
												variant="subtitle1"
												className={classes.userName}
											>
												{`${comment?.first_name} ${comment?.last_name}`}
											</Typography>
										</Grid>
										<Grid item xs={12}>
											<Typography
												noWrap
												variant="subtitle1"
												className={classes.commentDate}
												title={formatDate(
													comment?.update_time
														? comment?.update_time?.toString()
														: comment?.creation_time?.toString()
												)}
											>
												{formatDate(
													comment?.update_time
														? comment?.update_time?.toString()
														: comment?.creation_time?.toString()
												)}
											</Typography>
										</Grid>
									</Grid>
									<Grid item>
										<Typography variant="body2" className={classes.commentContent}>
											{comment?.comment}
										</Typography>
									</Grid>
									{user?.token?.userid === comment?.user_id && (
										<Grid item xs={12} container>
											<Grid item style={{ paddingRight: 8 }}>
												<Typography
													component="a"
													onClick={() => editComment(comment?.id, comment?.comment)}
													variant="body2"
													className={classes.commentAction}
												>
													{t("viewer.tabs.comment.edit")}
												</Typography>
											</Grid>
											<Grid item style={{ paddingLeft: 8 }}>
												<Typography
													component="a"
													onClick={() => deleteComment(comment?.id)}
													variant="body2"
													className={classes.commentAction}
												>
													{t("viewer.tabs.comment.delete")}
												</Typography>
											</Grid>
										</Grid>
									)}
								</Grid>
							</Grid>
						)
					})}
				{isNativeView ? (
					<>
						{annotations?.length > 0 &&
							annotations?.map((annotation, index) => {
								return (
									<Grid
										container
										xs={12}
										key={`${annotation?.pageID}-${index}`}
										style={{ background: theme.palette.grey["D1500"] }}
										className={classes.annoListContainer}
									>
										<Grid item xs={2}>
											<Avatar alt={"A"} {...stringAvatar(annotation?.user_name)} />
										</Grid>
										<Grid item xs={10} style={{ paddingLeft: 0 }}>
											<Grid item xs={12} container>
												<Grid item xs={8}>
													<Typography variant="subtitle1" className={classes.userName}>
														{annotation?.user_name}
													</Typography>
												</Grid>
												<Grid item xs={4} style={{ textAlign: "right" }}>
													<Typography
														component="span"
														variant="subtitle1"
														className={classes.commentDate}
														style={{ cursor: "pointer" }}
														onClick={() => {
															if (annotation?.page_number !== currentPageNumber)
																dispatch(
																	getFileRequestByPage({
																		id: props?.documentId,
																		page: annotation?.page_number,
																		reviewId:
																			props?.retainedFilterJobData?.additional_params?.review_id,
																	})
																)
															dispatch(updateViewerPageNumber(annotation?.page_number))
														}}
													>
														{`Page ${annotation?.page_number}`}
													</Typography>
												</Grid>
											</Grid>
											<Grid item>
												<Typography
													variant="body2"
													component="p"
													style={{ wordWrap: "break-word", wordBreak: "break-all" }}
												>
													{annotation?.quote}
												</Typography>
											</Grid>
											{user?.token?.userid === annotation?.user_id && (
												<Grid item xs={12} sm container>
													<Grid item>
														<Typography
															component="a"
															onClick={() => handleDeleteAnnotation(annotation?.id, isNativeView)}
															variant="body2"
															className={classes.commentAction}
															style={{ background: "none" }}
														>
															{t("viewer.tabs.comment.delete")}
														</Typography>
													</Grid>
												</Grid>
											)}
										</Grid>
									</Grid>
								)
							})}
					</>
				) : (
					<>
						{annotations?.length > 0 &&
							annotations?.map((annotation, index) => {
								return (
									<Grid
										container
										xs={12}
										key={`${annotation?.pageID}-${index}`}
										style={{ background: theme.palette.grey["D1500"] }}
										className={classes.annoListContainer}
									>
										<Grid item xs={2}>
											<Avatar alt={"A"} {...stringAvatar(annotation?.user_name)} />
										</Grid>
										<Grid item xs={10} style={{ paddingLeft: 0 }}>
											<Grid item xs={12} container>
												<Grid item xs={8}>
													<Typography variant="subtitle1" className={classes.userName}>
														{annotation?.user_name}
													</Typography>
												</Grid>
												<Grid item xs={4} style={{ textAlign: "right" }}>
													<Typography
														component="span"
														variant="subtitle1"
														className={classes.commentDate}
														style={{ cursor: "pointer" }}
														onClick={() => {
															if (annotation?.page_number !== currentPageNumber)
																dispatch(
																	getFileRequestByPage({
																		id: props?.documentId,
																		page: annotation?.page_number,
																		reviewId:
																			props?.retainedFilterJobData?.additional_params?.review_id,
																	})
																)
															dispatch(updateViewerPageNumber(annotation?.page_number))
														}}
													>
														{`Page ${annotation?.page_number}`}
													</Typography>
												</Grid>
											</Grid>
											<Grid item>
												<Typography
													variant="body2"
													component="p"
													style={{ wordWrap: "break-word", wordBreak: "break-all" }}
												>
													{annotation?.text}
												</Typography>
											</Grid>
											{user?.token?.userid === annotation?.user_id && (
												<Grid item xs={12} sm container>
													<Grid item>
														<Typography
															component="a"
															onClick={() => handleDeleteAnnotation(annotation?.id)}
															variant="body2"
															className={classes.commentAction}
															style={{ background: "none" }}
														>
															{t("viewer.tabs.comment.delete")}
														</Typography>
													</Grid>
												</Grid>
											)}
										</Grid>
									</Grid>
								)
							})}
					</>
				)}
				{_.isEmpty(comments) && _.isEmpty(annotations) && (
					<Typography variant="body2" component="p">
						{t("viewer.tabs.comment.noComment")}
					</Typography>
				)}
			</Box>
			<AddComment docId={props?.documentId} editComment={editCommentData} />
			<ConfirmationDialog
				open={open}
				title={t("confirmationDialog.comment.title")}
				message={t("confirmationDialog.comment.message")}
				onConfirm={onConfirm}
				onDismiss={onDismiss}
				btnText={t("confirmationDialog.comment.delete")}
			/>
		</Fragment>
	)
}

export default Comments
